import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Database, Scales } from '@phosphor-icons/react';
import MigrationCrunchFooter from '../components/MigrationCrunchFooter';

/**
 * Public Terms of Service. Covers MigrationCrunch + SalesCrunchCRM (shared
 * infrastructure). Plain language, founder-written. Designed to be readable
 * in under 10 minutes by a real human.
 *
 * Last reviewed: Feb 2026. If material changes are made (new payment terms,
 * new liability terms, new dispute mechanism), the "Last updated" date below
 * MUST be bumped, and existing customers should be notified by email if the
 * change reduces their rights.
 *
 * NOT legal advice. If a customer or regulator requests a formal version
 * for an enterprise procurement / vendor risk review, the user (Chip) should
 * retain counsel for that engagement.
 */

const LAST_UPDATED = 'February 16, 2026';

export default function TermsOfServicePage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Terms of Service — MigrationCrunch & SalesCrunchCRM';
    let metaDesc = document.querySelector('meta[name="description"]');
    let createdMeta = false;
    let prevDesc = null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
      createdMeta = true;
    } else {
      prevDesc = metaDesc.getAttribute('content');
    }
    metaDesc.setAttribute(
      'content',
      'Terms of Service for MigrationCrunch and SalesCrunchCRM. Plain English. No tricks.'
    );
    return () => {
      document.title = prevTitle;
      if (createdMeta) metaDesc.remove();
      else if (prevDesc !== null) metaDesc.setAttribute('content', prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0F0F1A] text-white" data-testid="terms-of-service-page">
      <header className="border-b border-white/10 px-6 py-4 sticky top-0 bg-[#0F0F1A]/95 backdrop-blur-sm z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link to="/migrate" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Database size={28} weight="fill" className="text-[#F39C12]" />
            <span className="text-xl font-black">
              Migration<span className="text-[#F39C12]">Crunch</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/migrate" className="text-white/70 hover:text-amber-300 transition-colors">Home</Link>
            <Link to="/migrate/compare" className="text-white/70 hover:text-amber-300 transition-colors">Compare</Link>
            <Link to="/contact-us" className="text-white/70 hover:text-amber-300 transition-colors">Contact</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="inline-flex items-center gap-2 text-amber-300 text-[10px] font-black uppercase tracking-widest mb-3">
          <Scales size={14} weight="fill" />
          Legal
        </div>
        <h1 className="text-4xl md:text-5xl font-black mb-2 leading-tight">Terms of Service</h1>
        <p className="text-sm text-white/50 mb-10">
          Last updated: {LAST_UPDATED}
        </p>

        <p className="text-base text-white/80 leading-relaxed mb-8">
          These terms (&quot;Terms&quot;) govern your use of MigrationCrunch and SalesCrunchCRM
          (the &quot;Service&quot;), both operated by Eugene R. (&quot;Chip&quot;) Tippmann Jr. (&quot;we&quot;, &quot;us&quot;).
          By creating an account or paying for any tier, you agree to these Terms. If
          you don&apos;t agree, don&apos;t use the Service. Questions? Email{' '}
          <a className="text-amber-300 hover:text-amber-200 underline" href="mailto:chip@migrationcrunch.com">
            chip@migrationcrunch.com
          </a>{' '}
          before agreeing.
        </p>

        <Section title="1. What you&apos;re buying">
          <p>
            We sell two things:
          </p>
          <ul className="space-y-2 mt-3">
            <Bullet>
              <strong>SalesCrunchCRM</strong> — a hosted customer-relationship-management
              application accessed via the web.
            </Bullet>
            <Bullet>
              <strong>MigrationCrunch</strong> — a one-time-fee data migration tool that
              moves contacts, notes, and activities from a source CRM into a destination
              CRM, with patent-pending AI note-to-contact extraction.
            </Bullet>
          </ul>
          <p className="mt-3">
            Pricing tiers, contact limits, and feature inclusions for each product are
            listed on the respective product pages and are part of these Terms by reference.
          </p>
        </Section>

        <Section title="2. Your account">
          <ul className="space-y-2 mt-3">
            <Bullet>You must be 16 or older to use the Service.</Bullet>
            <Bullet>You must provide a real, working email address.</Bullet>
            <Bullet>You&apos;re responsible for keeping your password secret.</Bullet>
            <Bullet>You&apos;re responsible for every action taken from your account, even if someone else does it.</Bullet>
            <Bullet>One person per account. No sharing the same login across multiple humans (it confuses our audit logs and your bills).</Bullet>
          </ul>
          <p className="mt-3">
            If we suspect your account is being used for spam, fraud, illegal activity,
            or violations of these Terms, we may suspend it without notice while we investigate.
          </p>
        </Section>

        <Section title="3. Payment, refunds, and renewals">
          <ul className="space-y-2 mt-3">
            <Bullet>
              <strong>SalesCrunchCRM (subscription):</strong> billed monthly or annually at
              the rate shown on your sign-up. We charge your card via Stripe at the
              start of each billing period. Cancel any time — you keep access until the
              end of the period already paid for, with no further charges.
            </Bullet>
            <Bullet>
              <strong>MigrationCrunch (one-time):</strong> charged once at purchase. No
              subscription. You keep your migration license for the data set you migrated.
            </Bullet>
            <Bullet>
              <strong>Money-back guarantee:</strong> MigrationCrunch carries a 30-day
              money-back guarantee specifically on the AI note-recovery feature. If our
              AI extraction quality doesn&apos;t meet what we claim on the product page, we
              refund your purchase in full. Email{' '}
              <a className="text-amber-300 hover:text-amber-200 underline" href="mailto:chip@migrationcrunch.com">
                chip@migrationcrunch.com
              </a>{' '}
              within 30 days of purchase to start a refund.
            </Bullet>
            <Bullet>
              <strong>Pro-rated refunds (SalesCrunchCRM):</strong> we don&apos;t pro-rate
              subscription refunds for unused time, but if you cancel within 14 days
              of your first paid month and you haven&apos;t imported substantial data, we&apos;ll
              refund that month on request.
            </Bullet>
            <Bullet>
              <strong>Price changes:</strong> we&apos;ll give you at least 30 days notice
              before raising the subscription price on an existing customer. Your next
              renewal after the notice will use the new price.
            </Bullet>
            <Bullet>
              <strong>Taxes:</strong> prices shown are exclusive of any sales tax, VAT,
              or GST that may apply in your jurisdiction.
            </Bullet>
          </ul>
        </Section>

        <Section title="4. Your data is yours">
          <p>
            Any data you upload, type, or sync into the Service — contacts, notes,
            activities, attachments — is your property. We don&apos;t claim ownership.
            We don&apos;t sell it. We don&apos;t train external AI models on it.
          </p>
          <p>
            We only use your data to operate the Service for you (run migrations, run
            features, send your transactional email, charge your card). For full detail
            see the{' '}
            <Link to="/privacy" className="text-amber-300 hover:text-amber-200 underline">
              Privacy Policy
            </Link>
            .
          </p>
          <p>
            You can export all of your data at any time via the export tools in the
            Service, or by emailing us.
          </p>
        </Section>

        <Section title="5. Acceptable use">
          <p>
            Don&apos;t use the Service to:
          </p>
          <ul className="space-y-2 mt-3">
            <Bullet>Send spam, phishing, or unsolicited marketing email at scale.</Bullet>
            <Bullet>Store or process data you don&apos;t have lawful permission to hold (stolen contact lists, leaked databases, etc.).</Bullet>
            <Bullet>Reverse-engineer, decompile, or attempt to extract the source code or model weights.</Bullet>
            <Bullet>Scrape or brute-force the API beyond published rate limits.</Bullet>
            <Bullet>Resell the Service to third parties without a written reseller agreement with us.</Bullet>
            <Bullet>Use the Service to violate any law in your jurisdiction (or in ours, the United States).</Bullet>
          </ul>
          <p className="mt-3">
            Violations may result in account suspension, termination, and (where serious)
            cooperation with law enforcement.
          </p>
        </Section>

        <Section title="6. The AI note-extraction feature">
          <p>
            MigrationCrunch uses large language models to extract entities (names, phones,
            emails, relationships) from unstructured note text. We market this as
            &quot;99% accurate&quot; based on internal benchmarks against representative datasets.
          </p>
          <ul className="space-y-2 mt-3">
            <Bullet>
              No AI is 100% perfect. We surface low-confidence extractions for human review
              rather than committing them silently — see Patent Provisional #1 (Confidence
              Visibility Mechanism) for technical detail.
            </Bullet>
            <Bullet>
              You agree to spot-check extracted entities after migration before relying on
              them for critical business operations.
            </Bullet>
            <Bullet>
              If our extraction quality is materially below what we claim, that triggers
              the 30-day money-back guarantee in Section 3 above.
            </Bullet>
          </ul>
        </Section>

        <Section title="7. Service availability">
          <p>
            We target 99% uptime for the Service but don&apos;t guarantee it. We use
            commercially reasonable efforts to maintain availability and to notify users
            of planned maintenance.
          </p>
          <p>
            For unplanned outages, we&apos;ll post status updates and apply service credits
            (proportional refunds) for any extended downtime affecting paying subscribers.
            Credits are issued on request — email us with your billing reference.
          </p>
        </Section>

        <Section title="8. Intellectual property">
          <p>
            The Service&apos;s software, AI prompts, user interface, brand marks, patent
            applications, and proprietary algorithms are owned by us. You receive a
            limited, non-exclusive, non-transferable license to use them while your
            account is active and in good standing.
          </p>
          <p>
            You may NOT replicate the Service, train AI models on the Service&apos;s output to
            create a competing migration tool, or hold yourself out as the source of the
            Service.
          </p>
        </Section>

        <Section title="9. Third-party services">
          <p>
            The Service integrates with third parties (Stripe for payments, OpenAI /
            Anthropic / Google via Emergent for AI, Resend for email, Cloudflare for
            hosting, MongoDB Atlas for data storage). Their terms govern your interactions
            with them. We&apos;ve picked them carefully but can&apos;t guarantee their behavior.
          </p>
        </Section>

        <Section title="10. Limitation of liability">
          <p>
            Our total liability to you under these Terms is capped at the amount you
            paid us in the 12 months immediately preceding the claim. We are NOT liable
            for indirect, incidental, consequential, special, or punitive damages
            (including lost profits, lost revenue, or lost goodwill).
          </p>
          <p>
            This cap doesn&apos;t apply to (a) our gross negligence or willful misconduct,
            (b) our indemnity obligations, or (c) anything that can&apos;t be limited under
            applicable consumer-protection law.
          </p>
        </Section>

        <Section title="11. Indemnification">
          <p>
            You agree to defend and indemnify us against claims brought by third parties
            arising from your unlawful use of the Service, your violation of these Terms,
            or your infringement of someone else&apos;s rights through the Service.
          </p>
        </Section>

        <Section title="12. Termination">
          <p>
            You can close your account at any time from the Settings page (or by emailing
            us). We can suspend or terminate your account if you violate these Terms,
            fail to pay, or expose the Service or other users to risk.
          </p>
          <p>
            On termination, your access stops. We retain your data for up to 30 days
            (for accidental-deletion recovery) and then delete it per the{' '}
            <Link to="/privacy" className="text-amber-300 hover:text-amber-200 underline">
              Privacy Policy
            </Link>{' '}
            schedule.
          </p>
        </Section>

        <Section title="13. Disputes and governing law">
          <p>
            These Terms are governed by the laws of the State of Indiana, United States.
            Any dispute we can&apos;t resolve informally will be brought in the state or
            federal courts located in Allen County, Indiana.
          </p>
          <p>
            Before suing us, please email{' '}
            <a className="text-amber-300 hover:text-amber-200 underline" href="mailto:chip@migrationcrunch.com">
              chip@migrationcrunch.com
            </a>{' '}
            with the subject &quot;Dispute&quot; and give us 30 days to make it right.
            Real humans answer that inbox. Most disputes are misunderstandings that get
            resolved with a refund.
          </p>
        </Section>

        <Section title="14. Changes to these Terms">
          <p>
            We may update these Terms over time. Material changes (anything that reduces
            your rights or expands your obligations) will be announced by email to account
            holders at least 30 days before they take effect. The &quot;Last updated&quot; date
            at the top of this page tells you when the current version was published.
          </p>
        </Section>

        <Section title="15. Contact">
          <p>
            Questions about these Terms? Email{' '}
            <a className="text-amber-300 hover:text-amber-200 underline" href="mailto:chip@migrationcrunch.com">
              chip@migrationcrunch.com
            </a>
            . A real person will answer.
          </p>
        </Section>
      </main>

      <MigrationCrunchFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8" data-testid={`terms-section-${title.toLowerCase().split(' ').slice(0, 2).join('-')}`}>
      <h2 className="text-xl md:text-2xl font-black mb-3 text-white">{title}</h2>
      <div className="text-sm md:text-base text-white/75 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex gap-3">
      <span className="text-amber-400 mt-1.5 flex-shrink-0">▸</span>
      <span>{children}</span>
    </li>
  );
}
